/*
 * CDS : restauration de la position de defilement au retour arriere.
 *
 * Probleme : quand le navigateur ne sert pas la page depuis le bfcache
 * (retour arriere apres une navigation classique, ou rechargement), il
 * restaure la position AVANT que les images en lazy-load aient donne sa
 * hauteur finale au document. La page est alors trop courte, le navigateur
 * plafonne le defilement, et la cliente se retrouve en haut de la grille
 * au lieu du produit qu'elle venait de quitter.
 *
 * Principe : on prend la main (history.scrollRestoration = 'manual'), on
 * memorise la position par URL dans sessionStorage, et au retour on
 * reessaie de la reappliquer image par image tant que le document n'est
 * pas assez haut, avec un plafond de temps.
 *
 * Contraintes tenues :
 * - aucun listener 'beforeunload' ni 'unload' (ils desactivent le bfcache) ;
 * - rien n'est fait quand la page revient du bfcache (le navigateur a deja
 *   restaure la position lui-meme) ;
 * - les entrees sessionStorage sont purgees (age et nombre) ;
 * - ES5, autonome, sans dependance, tout sous try/catch.
 */
(function () {
  'use strict';

  try {
    if (typeof window === 'undefined' || !window.document) return;
    if (!window.sessionStorage) return;
    if (!window.addEventListener) return;

    var STORE_KEY = 'cds_scroll_v1';
    var MAX_ENTRIES = 24;
    var MAX_AGE_MS = 21600000; // 6 heures
    var RETRY_BUDGET_MS = 1500;
    var TOLERANCE_PX = 2;

    function now() {
      return new Date().getTime();
    }

    function pageKey() {
      return window.location.pathname + window.location.search;
    }

    function currentY() {
      var y = window.pageYOffset;
      if (typeof y !== 'number') {
        y =
          (document.documentElement && document.documentElement.scrollTop) ||
          (document.body && document.body.scrollTop) ||
          0;
      }
      return y;
    }

    function readStore() {
      try {
        var raw = window.sessionStorage.getItem(STORE_KEY);
        if (!raw) return {};
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        return parsed;
      } catch (e) {
        return {};
      }
    }

    function writeStore(store) {
      try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
      } catch (e) {
        // Quota atteint ou stockage refuse : on repart d'une ardoise propre.
        try {
          window.sessionStorage.removeItem(STORE_KEY);
        } catch (e2) {}
      }
    }

    function prune(store) {
      var stamp = now();
      var keys = [];
      var key;
      var entry;

      for (key in store) {
        if (!Object.prototype.hasOwnProperty.call(store, key)) continue;
        entry = store[key];
        if (
          !entry ||
          typeof entry.y !== 'number' ||
          typeof entry.t !== 'number' ||
          stamp - entry.t > MAX_AGE_MS
        ) {
          delete store[key];
        } else {
          keys.push(key);
        }
      }

      if (keys.length > MAX_ENTRIES) {
        keys.sort(function (a, b) {
          return store[a].t - store[b].t;
        });
        var excess = keys.length - MAX_ENTRIES;
        for (var i = 0; i < excess; i++) {
          delete store[keys[i]];
        }
      }

      return store;
    }

    function save() {
      try {
        var y = currentY();
        if (typeof y !== 'number' || y < 0) return;
        var store = prune(readStore());
        store[pageKey()] = { y: Math.round(y), t: now() };
        writeStore(store);
      } catch (e) {}
    }

    function storedY() {
      try {
        var entry = readStore()[pageKey()];
        if (!entry || typeof entry.y !== 'number') return -1;
        return entry.y;
      } catch (e) {
        return -1;
      }
    }

    function navigationType() {
      try {
        if (window.performance && typeof window.performance.getEntriesByType === 'function') {
          var entries = window.performance.getEntriesByType('navigation');
          if (entries && entries.length && entries[0] && entries[0].type) {
            return entries[0].type;
          }
        }
      } catch (e) {}

      try {
        if (window.performance && window.performance.navigation) {
          var legacy = window.performance.navigation.type;
          if (legacy === 2) return 'back_forward';
          if (legacy === 1) return 'reload';
        }
      } catch (e) {}

      return '';
    }

    function raf(fn) {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(fn);
      } else {
        window.setTimeout(fn, 16);
      }
    }

    /* --- Memorisation ------------------------------------------------- */

    window.addEventListener('pagehide', save, false);
    document.addEventListener(
      'visibilitychange',
      function () {
        if (document.visibilityState === 'hidden') save();
      },
      false
    );

    /* --- Restauration -------------------------------------------------- */

    try {
      if (window.history && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (e) {}

    // Rien a restaurer hors retour arriere et rechargement, et on laisse la
    // priorite a l'ancre quand l'URL en porte une.
    var navType = navigationType();
    if (navType !== 'back_forward' && navType !== 'reload') return;
    if (window.location.hash) return;

    var target = storedY();
    if (target <= 0) return;

    var aborted = false;

    // Page servie par le bfcache : le navigateur a deja tout restaure.
    window.addEventListener(
      'pageshow',
      function (event) {
        if (event && event.persisted) aborted = true;
      },
      false
    );

    // Toute intervention de la cliente annule la restauration en cours :
    // on ne lui reprend jamais le defilement des mains.
    function giveUp() {
      aborted = true;
    }
    window.addEventListener('wheel', giveUp, true);
    window.addEventListener('touchstart', giveUp, true);
    window.addEventListener('keydown', giveUp, true);

    var deadline = now() + RETRY_BUDGET_MS;
    var settled = false;

    function maxScrollTop() {
      var docHeight = 0;
      if (document.documentElement && document.documentElement.scrollHeight) {
        docHeight = document.documentElement.scrollHeight;
      }
      if (document.body && document.body.scrollHeight > docHeight) {
        docHeight = document.body.scrollHeight;
      }
      var viewport =
        window.innerHeight ||
        (document.documentElement && document.documentElement.clientHeight) ||
        0;
      return docHeight - viewport;
    }

    function step() {
      if (aborted || settled) return;

      if (now() > deadline) {
        settled = true;
        return;
      }

      // Le lazy-load n'a pas encore donne sa hauteur finale au document :
      // on repasse a la frame suivante plutot que de plafonner le scroll.
      if (maxScrollTop() >= target) {
        try {
          window.scrollTo(0, target);
        } catch (e) {}
        if (Math.abs(currentY() - target) <= TOLERANCE_PX) {
          settled = true;
          return;
        }
      }

      raf(step);
    }

    raf(step);

    // Seconde passe apres 'load' : les images arrivees entre-temps peuvent
    // avoir decale le contenu.
    window.addEventListener(
      'load',
      function () {
        if (aborted) return;
        settled = false;
        deadline = now() + RETRY_BUDGET_MS;
        raf(step);
      },
      false
    );
  } catch (e) {}
})();
